import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { Dialog } from "../../../../components/Dialogs/Dialog";
import { WhatsAppTemplatePicker } from "../components/WhatsAppTemplatePicker";
import {
  CustomTextField,
  ControlledCustomSelect,
} from "../../../../components/formsComponents";
import { FileUpload } from "../../../../components/FileUpload/components/FileUpload";
import { PreviewTemplate } from "../../../../components/Preview/PreviewTemplate";
import { IWhatsAppTemplateData } from "../../../../redux/whatsappTemplate/whatsappTemplate.types";
import WhatsappBgImg from "../../../../assets/whatsapp_bg_img.jpg";
import { api } from "../../../../api/api";
import { useDispatchWrapper } from "../../../../hooks";
import { showMessage } from "../../../../redux/messages/messagesActions";
import { uploadFile } from "../../../../components/FileUpload/utils";
import { config } from "../../../../config";
import { IPaymentList } from "../../../../redux/paymentDetails/paymentDetails.types";
import { ChooseWhatsAppDIalog } from "../../../../components/ShareViaWhatsApp/ChooseWhatsAppDIalog";
import { IWAServiceProvider } from "../../../../components/ShareViaWhatsApp/ShareViaWhatsApp.types";
import { useAuth } from "../../../../contexts/AuthProvider";
import { fetchCampaignRuleTemplates } from "../services/paymentReminderConfig.service";

/** Column options available on the pending payment leads for variable mapping */
const MAPPING_KEY_OPTIONS = [
  { label: "Guest Name", value: "name_of_guest" },
  { label: "Contact Number", value: "contact_number_one" },
  { label: "Event Date", value: "function_date" },
  { label: "Event Type", value: "event_type" },
  { label: "Venue", value: "preferred_venue" },
  { label: "Remaining Balance", value: "remaining_balance" },
  { label: "Total Amount Paid", value: "total_amount_paid" },
];

interface ISendPaymentReminderDialogProps {
  open: boolean;
  onClose: () => void;
  selectedLeads: IPaymentList[];
  companyOutletUuid: string;
  companyOutletName?: string;
  onSendComplete?: () => void;
}

/**
 * Dialog flow:
 * 1. Select a WhatsApp template (reusable WhatsAppTemplatePicker)
 * 2. Fill required variables (mapped to lead columns or free text)
 * 3. Click Send -> opens Choose WhatsApp provider dialog (if multiple configured) -> sends
 */
export const SendPaymentReminderDialog: React.FC<ISendPaymentReminderDialogProps> = ({
  open,
  onClose,
  selectedLeads,
  companyOutletUuid,
  companyOutletName,
  onSendComplete,
}) => {
  const dispatch = useDispatchWrapper();
  const { whatsappService } = useAuth();

  const [selectedTemplate, setSelectedTemplate] = useState<IWhatsAppTemplateData | null>(null);
  const [moduleVariables, setModuleVariables] = useState<{ type: string; value: string }[]>([]);
  const [buttonVariableType, setButtonVariableType] = useState<string>("SELECT");
  const [buttonVariableValue, setButtonVariableValue] = useState<string>("");
  const [headerMediaFile, setHeaderMediaFile] = useState<File | null>(null);
  const [headerMediaType, setHeaderMediaType] = useState<string>("");
  const [showErrors, setShowErrors] = useState(false);
  const [sending, setSending] = useState(false);
  const [chooseProviderOpen, setChooseProviderOpen] = useState(false);

  const resetState = () => {
    setSelectedTemplate(null);
    setModuleVariables([]);
    setButtonVariableType("SELECT");
    setButtonVariableValue("");
    setHeaderMediaFile(null);
    setHeaderMediaType("");
    setShowErrors(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ---- Helpers ----
  const getBodyVariableCount = (template: IWhatsAppTemplateData | null) => {
    if (!template?.body?.text) return 0;
    const matches = template.body.text.match(/\{\{[^}]+\}\}/g);
    return matches ? matches.length : 0;
  };

  const hasButtonVariable = (template: IWhatsAppTemplateData | null): boolean => {
    if (!template?.buttons?.buttons?.length) return false;
    return template.buttons.buttons.some(
      (btn: any) => btn?.type?.toLowerCase() === "url",
    );
  };

  const hasHeaderMedia = (template: IWhatsAppTemplateData | null): boolean => {
    if (!template?.header?.format) return false;
    return ["IMAGE", "VIDEO", "DOCUMENT"].includes(template.header.format);
  };

  const handleTemplateSelect = (template: IWhatsAppTemplateData) => {
    setSelectedTemplate(template);
    setShowErrors(false);

    const bodyVarCount = getBodyVariableCount(template);
    setModuleVariables(Array.from({ length: bodyVarCount }, () => ({ type: "SELECT", value: "" })));

    if (template.header?.format && ["IMAGE", "VIDEO", "DOCUMENT"].includes(template.header.format)) {
      setHeaderMediaType(template.header.format);
    } else {
      setHeaderMediaType("");
    }

    if (hasButtonVariable(template)) {
      setButtonVariableType("SELECT");
      setButtonVariableValue("");
    }
  };

  const handleVariableChange = (index: number, field: "type" | "value", value: string) => {
    const updated = [...moduleVariables];
    if (field === "type") {
      updated[index] = { type: value, value: "" };
    } else {
      updated[index] = { ...updated[index], value };
    }
    setModuleVariables(updated);
  };

  const bodyVarCount = getBodyVariableCount(selectedTemplate);

  // Live preview values
  const bodyVariableValues: { [key: string]: string } = {};
  moduleVariables.forEach((v, i) => {
    if (v.type === "SELECT" && v.value) {
      bodyVariableValues[`${i + 1}`] = `{{${v.value}}}`;
    } else if (v.value) {
      bodyVariableValues[`${i + 1}`] = v.value;
    } else {
      bodyVariableValues[`${i + 1}`] = `{{${i + 1}}}`;
    }
  });

  const validateBeforeSend = (): boolean => {
    const hasUnfilledBodyVars = moduleVariables.some((v) => !v.value || v.value.trim() === "");
    const hasUnfilledButton = hasButtonVariable(selectedTemplate) && (!buttonVariableValue || buttonVariableValue.trim() === "");
    const hasUnfilledHeaderMedia = hasHeaderMedia(selectedTemplate) && !headerMediaFile;
    if (hasUnfilledBodyVars || hasUnfilledButton || hasUnfilledHeaderMedia) {
      setShowErrors(true);
      return false;
    }
    return true;
  };

  const handleSendClick = () => {
    if (!validateBeforeSend()) return;
    if (whatsappService.isMultiple) {
      setChooseProviderOpen(true);
    } else {
      doSend();
    }
  };

  const handleProviderSelect = (provider: IWAServiceProvider) => {
    setChooseProviderOpen(false);

    // Warn if BAILEY selected with >15 leads
    if (provider === "BAILEY" && selectedLeads.length > 15) {
      dispatch(
        showMessage({
          type: "warning",
          message:
            "Sending to more than 15 contacts via QR WhatsApp is not recommended. There are chances of your number being blocked. Consider using Meta WhatsApp instead.",
          displayAs: "snackbar",
        }),
      );
    }

    doSend(provider);
  };

  const doSend = async (provider?: IWAServiceProvider) => {
    setSending(true);
    try {
      // --- Step A: Fetch rule template (CUSTOM_PAYMENT_REMINDER) ---
      const templates = await fetchCampaignRuleTemplates(
        companyOutletUuid,
        "CUSTOM_PAYMENT_REMINDER",
      );
      const ruleTemplate = templates[0] || null;
      if (!ruleTemplate) {
        dispatch(
          showMessage({
            type: "error",
            message: "Custom reminder rule template not found. Please contact support.",
            displayAs: "snackbar",
          }),
        );
        setSending(false);
        return;
      }

      // --- Step B: Upload header media if a new file was provided ---
      let headerMediaValue = "";
      if (headerMediaFile && headerMediaType) {
        try {
          const path = await uploadFile({
            file: headerMediaFile,
            module_name: "WHATSAPP_MARKET_CAMPAIGN",
            defaultReturn: "",
            asPayload: {},
            onWarning: (message) => {
              dispatch(showMessage({ type: "warning", message, displayAs: "snackbar" }));
            },
          });
          headerMediaValue = `${config.assets.s3BaseUrl}/` + path;
        } catch {
          dispatch(showMessage({ type: "error", message: "Failed to upload header media", displayAs: "snackbar" }));
          setSending(false);
          return;
        }
      }

      // --- Step C: Build payload ---
      const leadsUuids = selectedLeads
        .map((l) => l.leads_uuid)
        .filter((uuid): uuid is string => !!uuid);

      const button_variable_column_name = hasButtonVariable(selectedTemplate)
        ? [{
            type: buttonVariableType,
            value: buttonVariableValue,
            sub_type: selectedTemplate?.buttons?.buttons?.[0]?.type?.toLowerCase() || "url",
          }]
        : null;

      const payload = {
        company_outlet_uuid: companyOutletUuid,
        campaign_rule: {
          leads_uuid: leadsUuids,
        },
        market_campaign_rule_template_uuid: ruleTemplate.market_campaign_rule_template_uuid,
        market_campaign_rule_template_name: ruleTemplate.template_name,
        whatsapp_template_uuid: selectedTemplate?.whatsapp_template_uuid || "",
        template_name: selectedTemplate?.name || "",
        header_media: headerMediaType
          ? [{ type: headerMediaType, value: headerMediaValue }]
          : null,
        button_variable_column_name,
        module_variables_columns_name: moduleVariables.length > 0 ? moduleVariables : null,
        platform: provider || whatsappService.selectedService || null,
      };

      // --- Step D: POST to new endpoint ---
      await api.post("/campaign/make-custom-reminder-campaign", payload);

      dispatch(
        showMessage({
          type: "success",
          message: `Reminder sent to ${selectedLeads.length} lead(s) successfully`,
          displayAs: "snackbar",
        }),
      );
      onSendComplete?.();
      handleClose();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to send reminders",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        size="lg"
        title="Send Payment Reminder"
        subtitle={`Sending to ${selectedLeads.length} selected lead(s)`}
        contentSx={{ minHeight: "60vh" }}
        actions={
          selectedTemplate
            ? [
                {
                  type: "button",
                  label: "Change Template",
                  variant: "outlined",
                  onClick: () => setSelectedTemplate(null),
                },
                {
                  type: "button",
                  label: sending ? "Sending..." : "Send",
                  variant: "contained",
                  color: "success",
                  startIcon: <Send />,
                  disabled: sending,
                  onClick: handleSendClick,
                },
              ]
            : undefined
        }
      >
        {/* Step 1: Template Selection */}
        {!selectedTemplate && (
          <Box>
            <Typography variant="h6" fontWeight={600} mb={1}>
              Select WhatsApp Template
            </Typography>
            <WhatsAppTemplatePicker
              companyOutletUuid={companyOutletUuid}
              onTemplateSelect={handleTemplateSelect}
            />
          </Box>
        )}

        {/* Step 2: Variable Mapping + Preview */}
        {selectedTemplate && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            {/* Left: Variables */}
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                {selectedTemplate.name}
              </Typography>

              {/* Header Media */}
              {hasHeaderMedia(selectedTemplate) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                    Header *
                  </Typography>
                  <Box
                    sx={
                      showErrors && !headerMediaFile
                        ? {
                            "& > div > div": {
                              borderColor: "error.main",
                            },
                          }
                        : undefined
                    }
                  >
                    <FileUpload
                      accept={
                        headerMediaType === "IMAGE" ? "image/*"
                          : headerMediaType === "VIDEO" ? "video/*"
                          : headerMediaType === "DOCUMENT" ? ".pdf"
                          : ""
                      }
                      disabled={sending}
                      onChange={(file: File) => setHeaderMediaFile(file)}
                      onDelete={() => setHeaderMediaFile(null)}
                      value={headerMediaFile?.name || ""}
                    />
                  </Box>
                  {showErrors && !headerMediaFile && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      Please upload the required media
                    </Typography>
                  )}
                </Box>
              )}

              {/* Body Variables */}
              {bodyVarCount > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                    Body Variables
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "primary.main" }}>
                        <TableRow>
                          <TableCell sx={{ color: "#fff", width: 40 }}>#</TableCell>
                          <TableCell sx={{ color: "#fff", width: 120 }}>Type</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {moduleVariables.map((variable, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <ControlledCustomSelect
                                fullWidth
                                disabled={sending}
                                value={variable.type}
                                options={[
                                  { label: "Text", value: "TEXT" },
                                  { label: "Select", value: "SELECT" },
                                ]}
                                onChange={(e) => handleVariableChange(index, "type", e.target.value as string)}
                              />
                            </TableCell>
                            <TableCell>
                              {variable.type === "TEXT" ? (
                                <CustomTextField
                                  fullWidth
                                  disabled={sending}
                                  value={variable.value}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleVariableChange(index, "value", e.target.value)
                                  }
                                  placeholder="Enter value"
                                  error={showErrors && !variable.value.trim()}
                                  helperText={showErrors && !variable.value.trim() ? "Required" : ""}
                                />
                              ) : (
                                <ControlledCustomSelect
                                  fullWidth
                                  disabled={sending}
                                  value={variable.value}
                                  options={MAPPING_KEY_OPTIONS}
                                  onChange={(e) => handleVariableChange(index, "value", e.target.value as string)}
                                  displayEmpty
                                  placeholder="Select column"
                                  error={showErrors && !variable.value}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Button Variables */}
              {hasButtonVariable(selectedTemplate) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                    Button Variables
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "primary.main" }}>
                        <TableRow>
                          <TableCell sx={{ color: "#fff", width: 40 }}>#</TableCell>
                          <TableCell sx={{ color: "#fff", width: 120 }}>Type</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>1</TableCell>
                          <TableCell>
                            <ControlledCustomSelect
                              fullWidth
                              value={buttonVariableType}
                              options={[
                                { label: "Text", value: "TEXT" },
                                { label: "Select", value: "SELECT" },
                              ]}
                              onChange={(e) => {
                                setButtonVariableType(e.target.value as string);
                                setButtonVariableValue("");
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {buttonVariableType === "TEXT" ? (
                              <CustomTextField
                                fullWidth
                                value={buttonVariableValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setButtonVariableValue(e.target.value)
                                }
                                placeholder={`e.g. ${selectedTemplate?.buttons?.buttons?.[0]?.example?.[0] || ""}`}
                                error={showErrors && !buttonVariableValue.trim()}
                                helperText={showErrors && !buttonVariableValue.trim() ? "Required" : ""}
                              />
                            ) : (
                              <ControlledCustomSelect
                                fullWidth
                                value={buttonVariableValue}
                                options={MAPPING_KEY_OPTIONS}
                                onChange={(e) => setButtonVariableValue(e.target.value as string)}
                                displayEmpty
                                placeholder="Select column"
                                error={showErrors && !buttonVariableValue}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {bodyVarCount === 0 && !hasButtonVariable(selectedTemplate) && !hasHeaderMedia(selectedTemplate) && (
                <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    This template has no variables to fill. You can send it directly.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right: Preview */}
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#efeae2",
                  backgroundImage: `url(${WhatsappBgImg})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "auto",
                  backgroundBlendMode: "multiply",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  minHeight: 300,
                }}
              >
                <PreviewTemplate
                  template={selectedTemplate}
                  bgImage={false}
                  headerMedia={headerMediaFile}
                  headerType={headerMediaType}
                  bodyVariableValues={bodyVariableValues}
                />
              </Box>
            </Box>
          </Stack>
        )}
      </Dialog>

      <ChooseWhatsAppDIalog
        open={chooseProviderOpen}
        onClose={() => setChooseProviderOpen(false)}
        onSelect={handleProviderSelect}
        providers={whatsappService.serviceProviders}
      />
    </>
  );
};
